/**
 * The keyboard's Return key walks a form in the native app.
 *
 * The iOS keyboard accessory bar (with its up/down "next field" arrows) is
 * hidden app-wide on purpose (useHideKeyboardAccessoryBar, #1579), which left
 * no way to move between fields. Instead, while a text field inside a form is
 * focused, Return is labelled "Next" and moves to the form's next text field;
 * on the last one it is labelled "Go" and submits as usual. The focused-field
 * reveal (keyboard.ts) scrolls the next field into view.
 *
 * A field that sets its own `enterKeyHint` is left alone, as are textareas
 * (Return is a newline there) and forms with a single field.
 */

const NON_TEXT_TYPES = new Set([
  'button',
  'checkbox',
  'color',
  'file',
  'hidden',
  'image',
  'radio',
  'range',
  'reset',
  'submit',
]);

function isTextInput(el: EventTarget | null): el is HTMLInputElement {
  return el instanceof HTMLInputElement && !NON_TEXT_TYPES.has(el.type);
}

function isUsable(el: HTMLInputElement): boolean {
  return !el.disabled && !el.readOnly && el.tabIndex !== -1 && el.getClientRects().length > 0;
}

/** The form's next usable text field after `el`, in document order. */
export function nextTextField(el: HTMLInputElement): HTMLInputElement | null {
  const form = el.form;
  if (!form) {
    return null;
  }

  const fields = Array.from(form.elements).filter(
    (field): field is HTMLInputElement => isTextInput(field) && (field === el || isUsable(field)),
  );
  const index = fields.indexOf(el);

  return index >= 0 ? (fields[index + 1] ?? null) : null;
}

/** Wires Return-to-next for the document; returns the cleanup. */
export function installFormReturnKey(doc: Document = document): () => void {
  // Hints we set ourselves, so a later focus can recompute them.
  const ours = new WeakSet<HTMLInputElement>();

  const onFocusIn = (event: FocusEvent) => {
    const el = event.target;
    if (!isTextInput(el) || !el.form) {
      return;
    }
    if (el.getAttribute('enterkeyhint') && !ours.has(el)) {
      return;
    }
    el.setAttribute('enterkeyhint', nextTextField(el) ? 'next' : 'go');
    ours.add(el);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Enter' || event.isComposing) {
      return;
    }
    const el = event.target;
    if (!isTextInput(el) || !ours.has(el)) {
      return;
    }
    const next = nextTextField(el);
    if (next) {
      event.preventDefault();
      next.focus();
    }
  };

  doc.addEventListener('focusin', onFocusIn);
  doc.addEventListener('keydown', onKeyDown);

  return () => {
    doc.removeEventListener('focusin', onFocusIn);
    doc.removeEventListener('keydown', onKeyDown);
  };
}
